import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import ProtectedRoute from '@/components/ProtectedRoute'
import ChunkErrorBoundary from '@/components/ChunkErrorBoundary'
import { useAuth } from '@/contexts/AuthContext'
import { canSeeTechnicianRoutes } from '@/lib/permissions'
import Layout from '@/components/layout/Layout'
import Login from '@/pages/Login'
import Unauthorized from '@/pages/Unauthorized'

// Public / lightweight — still lazy, but commonly hit
const Presentacion = lazy(() => import('@/pages/Presentacion'))
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'))
const ResetPassword = lazy(() => import('@/pages/ResetPassword'))

// App pages
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const Profile = lazy(() => import('@/pages/Profile'))
const Users = lazy(() => import('@/pages/Users'))
const Technicians = lazy(() => import('@/pages/Technicians'))
const IncidentTypes = lazy(() => import('@/pages/IncidentTypes'))
const IncidentStatuses = lazy(() => import('@/pages/IncidentStatuses'))
const Messages = lazy(() => import('@/pages/Messages'))
const Sites = lazy(() => import('@/pages/Sites'))
const Zones = lazy(() => import('@/pages/Zones'))
const SiteDetail = lazy(() => import('@/pages/SiteDetail'))
const Incidents = lazy(() => import('@/pages/Incidents'))
const IncidentsHistory = lazy(() => import('@/pages/IncidentsHistory'))
const LogisticsMap = lazy(() => import('@/pages/LogisticsMap'))
const TechnicianRoutesMapPage = lazy(() => import('@/pages/TechnicianRoutesMapPage'))
const Recaudacion = lazy(() => import('@/pages/Recaudacion'))
const RecaudacionDetalle = lazy(() => import('@/pages/RecaudacionDetalle'))
const SimStatistics = lazy(() => import('@/pages/SimStatistics'))
const AdminTimeTracking = lazy(() => import('@/pages/AdminTimeTracking'))
const Vacaciones = lazy(() => import('@/pages/Vacaciones'))
const LaravelLogs = lazy(() => import('@/pages/LaravelLogs'))
const MachinesPreview = lazy(() => import('@/pages/MachinesPreview'))
const Reports = lazy(() => import('@/pages/Reports'))

const STAFF = ['admin', 'gestor', 'encargado', 'administrativo']
const ADMIN = ['admin']
const RECAUDACION = ['admin', 'administrativo']
// Rol "soporte": acceso a Incidencias (libre), Mapa incidencias, Salones y Técnicos.
const STAFF_PLUS_SOPORTE = [...STAFF, 'soporte']
// El operador NO tiene Dashboard: aterriza directamente en Incidencias.
const DASHBOARD_ROLES = [...STAFF_PLUS_SOPORTE, 'tecnico']
// Salones: el técnico entra en modo consulta (sus zonas, sin editar).
const SITES_ROLES = [...STAFF_PLUS_SOPORTE, 'tecnico']

// Redirección de inicio según rol (operador → Incidencias, resto → Dashboard).
function HomeRedirect() {
  const { user } = useAuth()
  const role = String(user?.role || '').toLowerCase()
  return <Navigate to={role === 'operador' ? '/incidents' : '/dashboard'} replace />
}

function PageFallback() {
  return (
    <div className="flex h-[60vh] items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
    </div>
  )
}

const withSuspense = (Component) => (
  <ChunkErrorBoundary>
    <Suspense fallback={<PageFallback />}>
      <Component />
    </Suspense>
  </ChunkErrorBoundary>
)

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={withSuspense(ForgotPassword)} />
      <Route path="/reset-password" element={withSuspense(ResetPassword)} />
      <Route path="/presentacion" element={withSuspense(Presentacion)} />
      <Route path="/unauthorized" element={<Unauthorized />} />

      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomeRedirect />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute rolesAllowed={DASHBOARD_ROLES}>
              {withSuspense(Dashboard)}
            </ProtectedRoute>
          }
        />
        <Route path="/profile" element={withSuspense(Profile)} />
        <Route path="/incidents" element={withSuspense(Incidents)} />
        <Route path="/incidents/historial" element={withSuspense(IncidentsHistory)} />

        <Route
          path="/users"
          element={
            <ProtectedRoute rolesAllowed={STAFF}>
              {withSuspense(Users)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/technicians"
          element={
            <ProtectedRoute rolesAllowed={[...ADMIN, 'soporte']}>
              {withSuspense(Technicians)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/incident-types"
          element={
            <ProtectedRoute rolesAllowed={STAFF}>
              {withSuspense(IncidentTypes)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/incident-statuses"
          element={
            <ProtectedRoute rolesAllowed={STAFF}>
              {withSuspense(IncidentStatuses)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/sites"
          element={
            <ProtectedRoute rolesAllowed={SITES_ROLES}>
              {withSuspense(Sites)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/zones"
          element={
            <ProtectedRoute rolesAllowed={['admin', 'gestor']}>
              {withSuspense(Zones)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/sites/:id"
          element={
            <ProtectedRoute rolesAllowed={SITES_ROLES}>
              {withSuspense(SiteDetail)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/messages"
          element={
            <ProtectedRoute rolesAllowed={STAFF}>
              {withSuspense(Messages)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/logistics"
          element={
            <ProtectedRoute rolesAllowed={STAFF_PLUS_SOPORTE}>
              {withSuspense(LogisticsMap)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/technician-routes"
          element={
            <ProtectedRoute rolesAllowed={STAFF} allow={canSeeTechnicianRoutes}>
              {withSuspense(TechnicianRoutesMapPage)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/vacaciones"
          element={
            <ProtectedRoute rolesAllowed={ADMIN}>
              {withSuspense(Vacaciones)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/recaudacion"
          element={
            <ProtectedRoute rolesAllowed={RECAUDACION}>
              {withSuspense(Recaudacion)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/revenue/:id"
          element={
            <ProtectedRoute rolesAllowed={RECAUDACION}>
              {withSuspense(RecaudacionDetalle)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/sim/statistics"
          element={
            <ProtectedRoute rolesAllowed={ADMIN}>
              {withSuspense(SimStatistics)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/time-tracking"
          element={
            <ProtectedRoute rolesAllowed={ADMIN}>
              {withSuspense(AdminTimeTracking)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/logs"
          element={
            <ProtectedRoute rolesAllowed={ADMIN}>
              {withSuspense(LaravelLogs)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/machines-preview"
          element={
            <ProtectedRoute rolesAllowed={['admin', 'gestor']}>
              {withSuspense(MachinesPreview)}
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute rolesAllowed={['admin', 'gestor']}>
              {withSuspense(Reports)}
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  )
}
